import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import {
  AdminUserGraphQL,
  useAdminUsersRelayQuery,
} from '../../hooks/admins/query/V2/useAdminUsersRelay';
import { CHAT_BODY_MAX, transferNoteRoom } from './transferNote';

interface Props {
  open: boolean;
  /** The admin the session is assigned to now, left out of the choices. */
  currentAdminId: string | null;
  busy: boolean;
  onClose: () => void;
  /** `note` is the optional internal note for the next agent, trimmed ('' for none). */
  onAssign: (admin: AdminUserGraphQL, note: string) => void;
}

export const adminLabel = (
  admin: Pick<AdminUserGraphQL, 'firstName' | 'lastName' | 'email'>
) => `${admin.firstName ?? ''} ${admin.lastName ?? ''}`.trim() || admin.email;

/**
 * Picks another admin to hand the session to, with an optional internal
 * note for them (the transfer: why, and what has been tried). The list is ant's
 * listAdminUsers, searched on the server the same way the admin settings
 * page searches it, and only once the agent types: the unfiltered list
 * takes a minute on ant and can fail outright, so opening the dialog reads
 * nothing.
 */
export function AssignAdminDialog({
  open,
  currentAdminId,
  busy,
  onClose,
  onAssign,
}: Props) {
  const [input, setInput] = useState('');
  /** What the agent typed; picking an option rewrites `input`, not this. */
  const [typed, setTyped] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminUserGraphQL | null>(null);
  const [note, setNote] = useState('');

  // Search 300 ms after the agent stops typing (nothing to do while closed).
  useEffect(() => {
    const next = typed.trim();
    if (!open || next === search) return undefined;
    const t = setTimeout(() => setSearch(next), 300);
    return () => clearTimeout(t);
  }, [open, typed, search]);

  useEffect(() => {
    if (!open) {
      setInput('');
      setTyped('');
      setSearch('');
      setSelected(null);
      setNote('');
    }
  }, [open]);

  // The note goes out as "Transferred to <name>: <note>", within ant's body limit.
  const noteMax = selected ? transferNoteRoom(adminLabel(selected)) : CHAT_BODY_MAX;
  const noteTooLong = note.trim().length > noteMax;

  const filter = useMemo(() => (search ? { search } : undefined), [search]);
  const searching = open && Boolean(search);
  const { data, isLoading: queryLoading } = useAdminUsersRelayQuery(filter, {
    limit: 20,
    enabled: searching,
  });
  const isLoading = searching && queryLoading;
  // Nothing typed: no list, not the last search's answer.
  const found = searching ? (data ?? []).filter((a) => a.id !== currentAdminId) : [];
  // Keep the picked admin among the options so the field stays valid.
  const options =
    selected && !found.some((a) => a.id === selected.id) ? [selected, ...found] : found;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Assign to another admin</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mt: 1 }}
          options={options}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          inputValue={input}
          onInputChange={(_, value, reason) => {
            setInput(value);
            if (reason === 'input' || reason === 'clear') setTyped(value);
          }}
          filterOptions={(o) => o}
          getOptionLabel={(o) => `${adminLabel(o)} · ${o.email}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={isLoading}
          noOptionsText={search ? 'No admin matches' : 'Type a name or email'}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label="Admin"
              placeholder="Search by name or email"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          label="Note for the next agent (optional)"
          placeholder="What the rider needs and what has been tried"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: noteMax, 'aria-label': 'Transfer note' }}
          error={noteTooLong}
          helperText={
            noteTooLong
              ? `Shorten the note to ${noteMax.toLocaleString('en-US')} characters.`
              : 'Posted as an internal note. The rider never sees it.'
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || busy || noteTooLong}
          onClick={() => selected && onAssign(selected, note.trim())}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
